export interface USState {
    name: string;
    abbreviation: string;
    slug: string;
}

export const US_STATES: USState[] = [
    { name: 'Alabama', abbreviation: 'AL', slug: 'alabama' },
    { name: 'Alaska', abbreviation: 'AK', slug: 'alaska' },
    { name: 'Arizona', abbreviation: 'AZ', slug: 'arizona' },
    { name: 'Arkansas', abbreviation: 'AR', slug: 'arkansas' },
    {
        name: 'California',
        abbreviation: 'CA',
        slug: 'california',
    },
    { name: 'Colorado', abbreviation: 'CO', slug: 'colorado' },
    {
        name: 'Connecticut',
        abbreviation: 'CT',
        slug: 'connecticut',
    },
    { name: 'Delaware', abbreviation: 'DE', slug: 'delaware' },
    {
        name: 'Washington D.C.',
        abbreviation: 'DC',
        slug: 'washington-dc',
    },
    { name: 'Florida', abbreviation: 'FL', slug: 'florida' },
    { name: 'Georgia', abbreviation: 'GA', slug: 'georgia' },
    { name: 'Hawaii', abbreviation: 'HI', slug: 'hawaii' },
    { name: 'Idaho', abbreviation: 'ID', slug: 'idaho' },
    { name: 'Illinois', abbreviation: 'IL', slug: 'illinois' },
    { name: 'Indiana', abbreviation: 'IN', slug: 'indiana' },
    { name: 'Iowa', abbreviation: 'IA', slug: 'iowa' },
    { name: 'Kansas', abbreviation: 'KS', slug: 'kansas' },
    { name: 'Kentucky', abbreviation: 'KY', slug: 'kentucky' },
    { name: 'Louisiana', abbreviation: 'LA', slug: 'louisiana' },
    { name: 'Maine', abbreviation: 'ME', slug: 'maine' },
    { name: 'Maryland', abbreviation: 'MD', slug: 'maryland' },
    {
        name: 'Massachusetts',
        abbreviation: 'MA',
        slug: 'massachusetts',
    },
    { name: 'Michigan', abbreviation: 'MI', slug: 'michigan' },
    { name: 'Minnesota', abbreviation: 'MN', slug: 'minnesota' },
    {
        name: 'Mississippi',
        abbreviation: 'MS',
        slug: 'mississippi',
    },
    { name: 'Missouri', abbreviation: 'MO', slug: 'missouri' },
    { name: 'Montana', abbreviation: 'MT', slug: 'montana' },
    { name: 'Nebraska', abbreviation: 'NE', slug: 'nebraska' },
    { name: 'Nevada', abbreviation: 'NV', slug: 'nevada' },
    {
        name: 'New Hampshire',
        abbreviation: 'NH',
        slug: 'new-hampshire',
    },
    {
        name: 'New Jersey',
        abbreviation: 'NJ',
        slug: 'new-jersey',
    },
    {
        name: 'New Mexico',
        abbreviation: 'NM',
        slug: 'new-mexico',
    },
    {
        name: 'North Carolina',
        abbreviation: 'NC',
        slug: 'north-carolina',
    },
    {
        name: 'North Dakota',
        abbreviation: 'ND',
        slug: 'north-dakota',
    },
    { name: 'Ohio', abbreviation: 'OH', slug: 'ohio' },
    { name: 'Oklahoma', abbreviation: 'OK', slug: 'oklahoma' },
    { name: 'Oregon', abbreviation: 'OR', slug: 'oregon' },
    {
        name: 'Pennsylvania',
        abbreviation: 'PA',
        slug: 'pennsylvania',
    },
    {
        name: 'Rhode Island',
        abbreviation: 'RI',
        slug: 'rhode-island',
    },
    {
        name: 'South Carolina',
        abbreviation: 'SC',
        slug: 'south-carolina',
    },
    {
        name: 'South Dakota',
        abbreviation: 'SD',
        slug: 'south-dakota',
    },
    { name: 'Tennessee', abbreviation: 'TN', slug: 'tennessee' },
    { name: 'Texas', abbreviation: 'TX', slug: 'texas' },
    { name: 'Utah', abbreviation: 'UT', slug: 'utah' },
    { name: 'Vermont', abbreviation: 'VT', slug: 'vermont' },
    { name: 'Virginia', abbreviation: 'VA', slug: 'virginia' },
    {
        name: 'Washington',
        abbreviation: 'WA',
        slug: 'washington',
    },
    {
        name: 'West Virginia',
        abbreviation: 'WV',
        slug: 'west-virginia',
    },
    { name: 'Wisconsin', abbreviation: 'WI', slug: 'wisconsin' },
    { name: 'Wyoming', abbreviation: 'WY', slug: 'wyoming' },
];

export function getStateBySlug(slug: string): USState | null {
    return US_STATES.find((state) => state.slug === slug) || null;
}

export function generateStateTitle(stateName: string): string {
    return `<title>${stateName} Mortgage Rates Today | Zillow Home Loans</title>`;
}

export function generateStateMetaDescription(stateName: string): string {
    return `<meta name="description" content="View current ${stateName} mortgage rates and get a personalized rate quote for a VA, FHA, Conventional or Jumbo mortgage in ${stateName}." />`;
}

export function generateRobotsMetaTag(): string {
    return `<meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"/>`;
}

export function generateCanonicalLink(stateSlug?: string): string {
    const baseUrl = 'https://www.zillow.com/homeloans/mortgage-rates';
    const url = stateSlug ? `${baseUrl}/${stateSlug}/` : `${baseUrl}/`;
    return `<link rel="canonical" href="${url}"/>`;
}

export function generateStateHeadContent(
    stateName: string,
    stateSlug?: string
): string {
    return `${generateStateTitle(stateName)}${generateStateMetaDescription(
        stateName
    )}${generateRobotsMetaTag()}${generateCanonicalLink(stateSlug)}`;
}

export interface StateRatesResult {
    type: 'state-rates';
    stateData: USState;
}

export interface CmsPageResult {
    type: 'cms-page';
}

export interface NotFoundResult {
    type: 'not-found';
}

export type MortgageRatesPageDetection =
    | StateRatesResult
    | CmsPageResult
    | NotFoundResult;

export function detectMortgageRatesPageType(
    slug: string
): MortgageRatesPageDetection {
    // Handle null, undefined, or empty inputs
    if (!slug || typeof slug !== 'string' || slug.trim().length === 0) {
        return {
            type: 'cms-page',
        };
    }

    // Check for state by slug format (e.g., 'california', 'tennessee')
    const stateData = getStateBySlug(slug.toLowerCase());
    if (stateData) {
        return {
            type: 'state-rates',
            stateData,
        };
    }

    return {
        type: 'cms-page',
    };
}
